import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG Imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';

export interface CodeConfirmationConfig {
  title: string;
  message: string;
  expectedCode: string;
  codeLabel?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  confirmButtonIcon?: string;
  severity?: 'success' | 'info' | 'warn' | 'danger';
}

export interface CodeConfirmationResult {
  confirmed: boolean;
  enteredCode?: string;
}

/**
 * Componente de diálogo de confirmación que requiere ingresar un código específico
 * Implementa Atomic Design (Molécula level)
 * Reutilizable para acciones críticas que requieren confirmación explícita con código
 */
@Component({
  selector: 'app-code-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    MessageModule
  ],
  templateUrl: './code-confirmation-dialog.component.html',
  styleUrls: ['./code-confirmation-dialog.component.scss']
})
export class CodeConfirmationDialogComponent {
  @Input() visible: boolean = false;
  @Input() config: CodeConfirmationConfig = {
    title: 'Confirmar Acción',
    message: 'Para proceder, ingrese el código requerido:',
    expectedCode: '',
    codeLabel: 'Código',
    confirmButtonLabel: 'Confirmar',
    cancelButtonLabel: 'Cancelar',
    confirmButtonIcon: 'pi pi-check',
    severity: 'warn'
  };
  @Input() loading: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirmed = new EventEmitter<CodeConfirmationResult>();
  @Output() CANCELED = new EventEmitter<void>();

  // Component state
  enteredCode: string = '';
  showCodeError: boolean = false;

  get isValidCode(): boolean {
    return this.enteredCode.trim().toUpperCase() === this.config.expectedCode.toUpperCase();
  }

  get isEmissionCodeValid(): boolean {
  return !this.config.expectedCode && !!this.enteredCode && this.enteredCode.trim().length >= 5;
  }

  get confirmButtonClass(): string {
    const baseClass = '';
    switch (this.config.severity) {
      case 'danger':
        return `${baseClass} p-button-danger`;
      case 'warn':
        return `${baseClass} p-button-warning`;
      case 'success':
        return `${baseClass} p-button-success`;
      case 'info':
      default:
        return `${baseClass} p-button-info`;
    }
  }

  get messageClass(): string {
    switch (this.config.severity) {
      case 'danger':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'warn':
        return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'success':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'info':
      default:
        return 'text-blue-700 bg-blue-50 border-blue-200';
    }
  }

  onCodeInput(): void {
    this.showCodeError = false;
  }

  onConfirm(): void {
    if (this.config.expectedCode) {
      if (!this.isValidCode) {
        this.showCodeError = true;
        return;
      }
    } else {
      // Validación mínima para emisión: al menos 5 dígitos
      if (!this.isEmissionCodeValid) {
        this.showCodeError = true;
        return;
      }
    }

    this.confirmed.emit({
      confirmed: true,
      enteredCode: this.enteredCode.trim()
    });
  }

  onCancel(): void {
    this.CANCELED.emit();
    this.close();
  }

  onHide(): void {
    this.close();
  }

  private close(): void {
    this.enteredCode = '';
    this.showCodeError = false;
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // Método para resetear el estado del diálogo
  reset(): void {
    this.enteredCode = '';
    this.showCodeError = false;
  }
}
